import classNames from 'classnames'
import { ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'
import {
  RichText as PMRichText,
  defaultMarkMappings,
} from 'prosemirror-react-renderer'
import React, { PropsWithChildren } from 'react'

import { isHyphenatedLocale } from '../locale/i18n'

import { renderFormulas, renderText } from './formatting'
import {
  ContentfulHeading,
  IContentfulTable,
  Image,
  LandingPageType,
  IContentfulLargeLeadForm,
  IContentfulSmallLeadForm,
  IContentfulLandingSection,
} from './shared-types'
import { StaticPage } from './staticPage'

export interface RichTextEntryLinkReference
  extends ContentfulRichTextGatsbyReference {
  __typename:
    | 'ContentfulBlogPost'
    | 'ContentfulStaticArticle'
    | 'ContentfulCategoryLandingPage'
    | 'ContentfulMethodPage'
    | 'ContentfulTeamMemberPage'
  slug?: string
  key?: string
  pageId?: StaticPage
}

export interface RichTextLandingLinkReference
  extends Omit<RichTextEntryLinkReference, '__typename'> {
  __typename: 'ContentfulLandingPage'
  pageType?: LandingPageType
}

export interface RichTextEntryReference
  extends Omit<ContentfulRichTextGatsbyReference, '__typename'>,
    ContentfulHeading,
    Omit<IContentfulTable, 'internal'>,
    Omit<IContentfulLargeLeadForm, 'internal'>,
    Omit<IContentfulLandingSection, 'internal'>,
    Omit<IContentfulSmallLeadForm, 'internal'> {
  __typename:
    | 'ContentfulHeading'
    | 'ContentfulTableOfContents'
    | 'ContentfulTable'
    | 'ContentfulLargeLeadForm'
    | 'ContentfulSmallLeadForm'
    | 'ContentfulLandingSection'
    | 'ContentfulTeamMemberPage'
}

export type ContentfulLinkReference =
  | RichTextEntryLinkReference
  | RichTextLandingLinkReference
  | undefined

export interface RichTextAssetReferences
  extends ContentfulRichTextGatsbyReference {
  description?: string
  gatsbyImageData?: Image
  title?: string
}

const markMappings = {
  ...defaultMarkMappings,
  bold: props => <strong {...props} />,
  highlight: ({ color, ...props }) => (
    <mark
      {...props}
      style={{ backgroundColor: color ? String(color) : undefined }}
    />
  ),
  subscript: props => <sub {...props} />,
  superscript: props => <sup {...props} />,
} satisfies Record<string, React.ElementType<Record<string, unknown>>>

// Node mappings that are used for rendering RichText
// (some of these may be used for contentful rich text as well, `see ./src/logic/contentful.tsx`)
export const nodeMappings = {
  bulletList: props => (
    <ul
      className={classNames('richText__list', 'richText__list--unordered')}
      {...props}
    />
  ),
  doc: props => <div className="richText" {...props} />,
  listItem: props => <li className="richText__listItem" {...props} />,
  orderedList: props => (
    <ol
      className={classNames('richText__list', 'richText__list--ordered')}
      {...props}
    />
  ),
  paragraph: props => {
    if (
      Array.isArray(props.children) &&
      props.children.every(child => Array.isArray(child) && child.length === 0)
    ) {
      return null
    }
    return (
      <p
        className={classNames('richText__paragraph', {
          hyphenate: isHyphenatedLocale(),
        })}
        {...props}
      />
    )
  },
  table: ({ children, ...props }: PropsWithChildren) => (
    <div className="richText__tableScrollWrapper">
      <table className="richText__table" {...props}>
        <tbody>{children}</tbody>
      </table>
    </div>
  ),
  tableCell: props => <td {...props} />,
  tableHeader: props => <th {...props} />,
  tableRow: props => <tr {...props} />,
} satisfies Record<string, React.ElementType<Record<string, unknown>>>

export const RichText = ({
  children,
  ...props
}: Omit<React.ComponentProps<typeof PMRichText>, 'children'> & {
  children: string
}) => {
  return (
    <PMRichText
      nodes={nodeMappings}
      marks={markMappings}
      renderText={renderText(renderFormulas)}
      {...props}
    >
      {JSON.parse(children)}
    </PMRichText>
  )
}
